import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { filterHelpDocument, getHelpDocument } from './helpData';
import type { HelpCategory, HelpDocument, HelpSearchResults } from './helpData';

export interface UseHelpDataResult {
    doc: HelpDocument;
    /** Instant search term; filters names, descriptions, tags, ids and shortcuts. */
    query: string;
    setQuery: (query: string) => void;
    /** Active sidebar category id (`all`, `fundamentals`, command categories, …). */
    categoryId: string;
    setCategoryId: (categoryId: string) => void;
    results: HelpSearchResults;
}

/**
 * Display label for a sidebar category in the active language: the locale's
 * `help.categories.<id>` translation when present, else the dataset's English
 * label via `defaultValue`.
 */
export function useHelpCategoryLabel(): (category: HelpCategory) => string {
    const { t } = useTranslation();
    return useCallback(
        (category: HelpCategory) => t(`help.categories.${category.id}`, { defaultValue: category.label }),
        [t],
    );
}

/**
 * Data provider for the Help Center: lazily parses the live Help & Capability
 * document (docs/help/yap-pro-helpdoc.md) once, then derives the visible
 * result set from the active category and search term on every keystroke.
 */
export function useHelpData(): UseHelpDataResult {
    const doc = useMemo(() => getHelpDocument(), []);
    const [query, setQuery] = useState('');
    const [categoryId, setCategoryId] = useState('all');
    const results = useMemo(
        () => filterHelpDocument(doc, categoryId, query),
        [doc, categoryId, query],
    );
    return { doc, query, setQuery, categoryId, setCategoryId, results };
}
